import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import ConfirmModal from './ConfirmModal';
import { AlarmSet, Alarm, Class } from '../types/alarm';
import { getDayName } from '../utils/alarmUtils';
import { userDataService } from '../services/userDataService';
import { TimeTagService } from '../services/timeTagService';

export type DayOfWeek = '일' | '월' | '화' | '수' | '목' | '금' | '토';

export interface TimeTag {
  id: string;
  name: string;
  startTime: string; // HH:MM 형식
  endTime: string;   // HH:MM 형식
  dayOfWeek: DayOfWeek[]; // 요일 정보 (배열)
  createdAt: Date;
  updatedAt: Date;
}

interface TimeTagSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (timeTags: TimeTag[]) => void;
  initialTimeTags?: TimeTag[];
  alarmSets?: AlarmSet[];
  onCreateAlarms?: (alarms: { alarm: Alarm; setIndex: number; dayIndex: number }[]) => Promise<void>;
  userId?: string;
  classId?: string;
  /** 기본 환경설정 모달 내 섹션으로 렌더 (모달 래퍼 없음) */
  renderAsSection?: boolean;
  /** 섹션 모드 시 타임태그 변경 시 부모에 알림 (저장은 부모가 담당) */
  onTimeTagsChange?: (timeTags: TimeTag[]) => void;
}

const TimeTagSettingsModal: React.FC<TimeTagSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTimeTags = [],
  alarmSets = [],
  onCreateAlarms,
  userId,
  classId,
  renderAsSection = false,
  onTimeTagsChange
}) => {
  const [timeTags, setTimeTags] = useState<TimeTag[]>(initialTimeTags);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newDuration, setNewDuration] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([]);
  const [isStartAm, setIsStartAm] = useState(true);
  const [isEndAm, setIsEndAm] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [startHourInput, setStartHourInput] = useState('');
  const [startMinuteInput, setStartMinuteInput] = useState('');
  const [endHourInput, setEndHourInput] = useState('');
  const [endMinuteInput, setEndMinuteInput] = useState('');
  const [isDurationDisabled, setIsDurationDisabled] = useState(false);
  const [isEndTimeDisabled, setIsEndTimeDisabled] = useState(false);
  const [activeTimeInput, setActiveTimeInput] = useState<'duration' | 'endTime' | null>(null);
  const [editingTag, setEditingTag] = useState<TimeTag | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [dayToDelete, setDayToDelete] = useState<DayOfWeek | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | '일' | '월' | '화' | '수' | '목' | '금' | '토'>('all');
  const [initialTimeTagsState, setInitialTimeTagsState] = useState<TimeTag[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // 알람 만들기 모달 관련 상태
  const [isCreateAlarmModalOpen, setIsCreateAlarmModalOpen] = useState(false);
  const [selectedAlarmSetIndex, setSelectedAlarmSetIndex] = useState<number>(0);
  const [selectedCreateDays, setSelectedCreateDays] = useState<number[]>([]);
  const [showCreateAlarmConfirm, setShowCreateAlarmConfirm] = useState(false);
  
  // 다른 클래스 데이터 가져오기 관련 states
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [selectedSourceClassId, setSelectedSourceClassId] = useState<string>('');
  const [sourceTimeTags, setSourceTimeTags] = useState<TimeTag[]>([]);
  const [selectedTimeTagIds, setSelectedTimeTagIds] = useState<Set<string>>(new Set());
  const [isLoadingSourceTimeTags, setIsLoadingSourceTimeTags] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  
  // 다른 클래스 데이터 가져오기 버튼 활성화 상태 (기본값: off)
  const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(false);
  
  // 로컬 스토리지 변경 감지
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      setIsImportButtonEnabled(saved !== null ? saved === 'true' : false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('importButtonEnabledChanged', handleStorageChange);

    // 주기적으로 확인 (같은 탭에서의 변경 감지용)
    const interval = setInterval(() => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      const currentValue = saved !== null ? saved === 'true' : false;
      setIsImportButtonEnabled(prev => {
        if (prev !== currentValue) {
          return currentValue;
        }
        return prev;
      });
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('importButtonEnabledChanged', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // 초기 동기화 시 부모 알림 방지 (섹션 모드 무한 루프 방지)
  const isSyncingFromParentRef = useRef(false);
  const lastNotifiedTimeTagsRef = useRef<TimeTag[] | null>(null);

  useEffect(() => {
    if (isOpen || renderAsSection) {
      // 기존 데이터 마이그레이션: dayOfWeek가 단일 값인 경우 배열로 변환
      const migratedTimeTags = initialTimeTags.map(tag => ({
        ...tag,
        dayOfWeek: Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek]
      }));
      // 섹션 모드: 부모가 우리가 보낸 데이터를 다시 넘긴 경우 스킵 (무한 루프 방지)
      if (renderAsSection && lastNotifiedTimeTagsRef.current !== null) {
        const lastNotified = lastNotifiedTimeTagsRef.current;
        if (lastNotified.length === migratedTimeTags.length &&
            lastNotified.every((t, i) => t.id === migratedTimeTags[i]?.id && t.name === migratedTimeTags[i]?.name)) {
          return;
        }
      }
      lastNotifiedTimeTagsRef.current = null;
      isSyncingFromParentRef.current = true;
      setTimeTags(migratedTimeTags);
      setInitialTimeTagsState(migratedTimeTags);
      setShowCancelConfirm(false);
    }
  }, [isOpen, initialTimeTags, renderAsSection]);

  // 섹션 모드: 타임태그 변경 시 부모에 알림 (부모에서 넘어온 동기화 직후에는 스킵)
  useEffect(() => {
    if (renderAsSection && onTimeTagsChange && !isSyncingFromParentRef.current) {
      lastNotifiedTimeTagsRef.current = timeTags;
      onTimeTagsChange(timeTags);
    }
    isSyncingFromParentRef.current = false;
  }, [renderAsSection, onTimeTagsChange, timeTags]);

  // 변경사항 감지 함수
  const hasChanges = useMemo(() => {
    if (initialTimeTagsState.length !== timeTags.length) return true;
    
    // 각 태그를 비교 (id, name, startTime, endTime, dayOfWeek)
    const initialMap = new Map(initialTimeTagsState.map(tag => [tag.id, tag]));
    const currentMap = new Map(timeTags.map(tag => [tag.id, tag]));
    
    // 태그 개수가 다르면 변경사항 있음
    if (initialMap.size !== currentMap.size) return true;
    
    // 각 태그 비교
    for (const [id, currentTag] of Array.from(currentMap)) {
      const initialTag = initialMap.get(id);
      if (!initialTag) return true; // 새 태그 추가됨
      
      // 속성 비교
      if (initialTag.name !== currentTag.name ||
          initialTag.startTime !== currentTag.startTime ||
          initialTag.endTime !== currentTag.endTime) {
        return true;
      }
      
      // dayOfWeek 배열 비교
      const initialDays = Array.isArray(initialTag.dayOfWeek) 
        ? [...initialTag.dayOfWeek].sort() 
        : [initialTag.dayOfWeek].sort();
      const currentDays = Array.isArray(currentTag.dayOfWeek) 
        ? [...currentTag.dayOfWeek].sort() 
        : [currentTag.dayOfWeek].sort();
      
      if (initialDays.length !== currentDays.length) return true;
      if (initialDays.some((day, idx) => day !== currentDays[idx])) return true;
    }
    
    // 삭제된 태그 확인
    for (const [id] of Array.from(initialMap)) {
      if (!currentMap.has(id)) return true;
    }
    
    return false;
  }, [initialTimeTagsState, timeTags]);

  // 취소 핸들러
  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  // 시간 계산 함수들
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const DEFAULT_START_TIME = '00:00';
  const getEffectiveStartTime = (startTime: string) => startTime || DEFAULT_START_TIME;

  const updateEndInputsFromTime = (time: string) => {
    setNewEndTime(time);
    if (!time) {
      setEndHourInput('');
      setEndMinuteInput('');
      setIsEndAm(true);
      return;
    }
    const [endHour, endMinute] = time.split(':').map(Number);
    const endIsAm = endHour < 12;
    setIsEndAm(endIsAm);
    setEndHourInput(endIsAm ? (endHour === 0 ? '12' : endHour.toString()) : (endHour === 12 ? '12' : (endHour - 12).toString()));
    setEndMinuteInput(endMinute.toString().padStart(2, '0'));
  };

  const updateDurationFromEndTime = (startTime: string, endTime: string) => {
    if (!endTime) {
      setNewDuration('');
      return;
    }
    const startMinutes = timeToMinutes(getEffectiveStartTime(startTime));
    const endMinutes = timeToMinutes(endTime);
    const durationMinutes = endMinutes - startMinutes;
    if (durationMinutes >= 0) {
      setNewDuration(durationMinutes.toString());
    }
  };

  const updateEndTimeFromDuration = (startTime: string, durationValue: string) => {
    if (durationValue === '') {
      updateEndInputsFromTime('');
      return;
    }
    const durationMinutes = parseInt(durationValue, 10);
    if (isNaN(durationMinutes)) return;
    const startMinutes = timeToMinutes(getEffectiveStartTime(startTime));
    const endMinutes = startMinutes + durationMinutes;
    updateEndInputsFromTime(minutesToTime(endMinutes));
  };

  const syncByStartTime = (nextStartTime: string) => {
    if (activeTimeInput === 'duration' || (!activeTimeInput && newDuration)) {
      updateEndTimeFromDuration(nextStartTime, newDuration);
    } else if (activeTimeInput === 'endTime' || (!activeTimeInput && newEndTime)) {
      updateDurationFromEndTime(nextStartTime, newEndTime);
    }
  };

  // 시간 입력 핸들러들
  const handleStartHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || !/^\d*$/.test(value)) {
      setStartHourInput(value);
      if (!value && !startMinuteInput) {
        setNewStartTime('');
        syncByStartTime('');
      }
      return;
    }

    setStartHourInput(value);

    let hour = parseInt(value);
    if (isNaN(hour)) return;

    // 12를 넘는 숫자를 입력했을 때만 토글을 오후로 변경
    if (hour > 12) {
      setIsStartAm(false);
    }

    // 12시간 형식을 24시간 형식으로 변환
    let newHour = hour;
    if (!isStartAm && hour < 12) {
      newHour = hour + 12;
    } else if (isStartAm && hour === 12) {
      newHour = 0;
    }

    const minutes = startMinuteInput || '00';
    const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
    setNewStartTime(newTime);
    syncByStartTime(newTime);
  };

  const handleStartMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || !/^\d*$/.test(value)) {
      setStartMinuteInput(value);
      if (!value && !startHourInput) {
        setNewStartTime('');
        syncByStartTime('');
      }
      return;
    }

    setStartMinuteInput(value);

    if (value.length === 2) {
      const minute = parseInt(value);
      if (!isNaN(minute) && minute >= 0 && minute <= 59) {
        const hours = startHourInput || '00';
        let hour = parseInt(hours);
        if (!isStartAm && hour < 12) {
          hour = hour + 12;
        } else if (isStartAm && hour === 12) {
          hour = 0;
        }
        const newTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        setNewStartTime(newTime);
        syncByStartTime(newTime);
      }
    }
  };

  const handleEndMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 비활성화 상태에서는 입력 무시
    if (isEndTimeDisabled) return;

    const value = e.target.value;

    if (value === '' || !/^\d*$/.test(value)) {
      setEndMinuteInput(value);
      if (!value && !endHourInput) {
        setActiveTimeInput(null);
        setIsDurationDisabled(false);
        updateEndInputsFromTime('');
        setNewDuration('');
      }
      return;
    }

    setEndMinuteInput(value);

    if (value.length === 2) {
      const minute = parseInt(value);
      if (!isNaN(minute) && minute >= 0 && minute <= 59) {
        const hours = endHourInput || '00';
        let hour = parseInt(hours);
        if (!isEndAm && hour < 12) {
          hour = hour + 12;
        } else if (isEndAm && hour === 12) {
          hour = 0;
        }
        const newTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        setActiveTimeInput('endTime');
        updateEndInputsFromTime(newTime);
        updateDurationFromEndTime(newStartTime, newTime);
        setIsDurationDisabled(true);
      }
    }
  };

  const handleStartHourBlur = () => {
    const hour = parseInt(startHourInput);
    if (!isNaN(hour) && hour > 12) {
      const normalizedHour = hour % 12 || 12;
      setStartHourInput(normalizedHour.toString());

      let newHour = normalizedHour;
      if (!isStartAm && normalizedHour < 12) {
        newHour = normalizedHour + 12;
      } else if (isStartAm && normalizedHour === 12) {
        newHour = 0;
      }

      const minutes = startMinuteInput || '00';
      const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
      setNewStartTime(newTime);
    syncByStartTime(newTime);
    }
  };

  const handleStartMinuteBlur = () => {
    const minute = parseInt(startMinuteInput);
    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      setStartMinuteInput(minute.toString().padStart(2, '0'));

      const hours = startHourInput || '00';
      let hour = parseInt(hours);
      if (!isStartAm && hour < 12) {
        hour = hour + 12;
      } else if (isStartAm && hour === 12) {
        hour = 0;
      }
      const newTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      setNewStartTime(newTime);
      syncByStartTime(newTime);
    }
  };

  // 활동시간 입력 핸들러
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewDuration(value);
    if (value === '') {
      setActiveTimeInput(null);
      setIsEndTimeDisabled(false);
      setIsDurationDisabled(false);
      updateEndInputsFromTime('');
      return;
    }

    setActiveTimeInput('duration');
    updateEndTimeFromDuration(newStartTime, value);
    setIsEndTimeDisabled(true);
    setIsDurationDisabled(false);
  };

  // 종료시간 입력 핸들러
  const handleEndHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 비활성화 상태에서는 입력 무시
    if (isEndTimeDisabled) return;

    const value = e.target.value;

    if (value === '' || !/^\d*$/.test(value)) {
      setEndHourInput(value);
      if (!value && !endMinuteInput) {
        setActiveTimeInput(null);
        setIsDurationDisabled(false);
        updateEndInputsFromTime('');
        setNewDuration('');
      }
      return;
    }

    setEndHourInput(value);

    let hour = parseInt(value);
    if (isNaN(hour)) return;

    // 12를 넘는 숫자를 입력했을 때만 토글을 오후로 변경
    if (hour > 12) {
      setIsEndAm(false);
    }

    // 12시간 형식을 24시간 형식으로 변환
    let newHour = hour;
    if (!isEndAm && hour < 12) {
      newHour = hour + 12;
    } else if (isEndAm && hour === 12) {
      newHour = 0;
    }

    const minutes = endMinuteInput || '00';
    const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
    setActiveTimeInput('endTime');
    updateEndInputsFromTime(newTime);
    updateDurationFromEndTime(newStartTime, newTime);
    setIsDurationDisabled(true);
  };

  const handleEndHourBlur = () => {
    const hour = parseInt(endHourInput);
    if (!isNaN(hour) && hour > 12) {
      const normalizedHour = hour % 12 || 12;
      setEndHourInput(normalizedHour.toString());

      let newHour = normalizedHour;
      if (!isEndAm && normalizedHour < 12) {
        newHour = normalizedHour + 12;
      } else if (isEndAm && normalizedHour === 12) {
        newHour = 0;
      }

      const minutes = endMinuteInput || '00';
      const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
    setActiveTimeInput('endTime');
    updateEndInputsFromTime(newTime);
    updateDurationFromEndTime(newStartTime, newTime);
    setIsDurationDisabled(true);
    }
  };

  const handleEndMinuteBlur = () => {
    const minute = parseInt(endMinuteInput);
    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      setEndMinuteInput(minute.toString().padStart(2, '0'));

      const hours = endHourInput || '00';
      let hour = parseInt(hours);
      if (!isEndAm && hour < 12) {
        hour = hour + 12;
      } else if (isEndAm && hour === 12) {
        hour = 0;
      }
      const newTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      setActiveTimeInput('endTime');
      updateEndInputsFromTime(newTime);
      updateDurationFromEndTime(newStartTime, newTime);
      setIsDurationDisabled(true);
    }
  };

  // 초기화 핸들러들
  const handleResetDuration = () => {
    setNewDuration('');
    setActiveTimeInput(null);
    setIsDurationDisabled(false);
    setIsEndTimeDisabled(false);
    updateEndInputsFromTime('');
  };

  const handleResetEndTime = () => {
    setActiveTimeInput(null);
    updateEndInputsFromTime('');
    setNewDuration('');
    setIsDurationDisabled(false);
    setIsEndTimeDisabled(false);
  };

  const handleResetStartTime = () => {
    setNewStartTime('');
    setStartHourInput('');
    setStartMinuteInput('');
    setIsStartAm(true);
    syncByStartTime('');
  };

  const handleToggleStartAm = () => {
    const nextIsAm = !isStartAm;
    setIsStartAm(nextIsAm);
    if (!startHourInput && !startMinuteInput) {
      syncByStartTime('');
      return;
    }
    const hour = parseInt(startHourInput || '0');
    if (isNaN(hour)) return;
    let newHour = hour;
    if (!nextIsAm && hour < 12) {
      newHour = hour + 12;
    } else if (nextIsAm && hour === 12) {
      newHour = 0;
    }
    const minutes = startMinuteInput || '00';
    const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
    setNewStartTime(newTime);
    syncByStartTime(newTime);
  };

  const handleToggleEndAm = () => {
    if (isEndTimeDisabled) return;
    const nextIsAm = !isEndAm;
    setIsEndAm(nextIsAm);
    if (!endHourInput && !endMinuteInput) return;
    const hour = parseInt(endHourInput || '0');
    if (isNaN(hour)) return;
    let newHour = hour;
    if (!nextIsAm && hour < 12) {
      newHour = hour + 12;
    } else if (nextIsAm && hour === 12) {
      newHour = 0;
    }
    const minutes = endMinuteInput || '00';
    const newTime = `${newHour.toString().padStart(2, '0')}:${minutes}`;
    setActiveTimeInput('endTime');
    updateEndInputsFromTime(newTime);
    updateDurationFromEndTime(newStartTime, newTime);
    setIsDurationDisabled(true);
  };

  // 요일 토글 함수
  const toggleDay = (day: DayOfWeek) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  // 전체 요일 선택/해제
  const toggleAllDays = () => {
    const allDays: DayOfWeek[] = ['일', '월', '화', '수', '목', '금', '토'];
    setSelectedDays(prev =>
      prev.length === 7 ? [] : allDays
    );
  };

  const handleAddTag = () => {
    const startTimeToSave = newStartTime || DEFAULT_START_TIME;
    if (!newTagName.trim() || !newEndTime || selectedDays.length === 0) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    // 시간 유효성 검사
    if (startTimeToSave >= newEndTime) {
      alert('시작 시간은 끝나는 시간보다 빨라야 합니다.');
      return;
    }

    if (editingTag) {
      // 수정 모드 - 선택된 요일들로만 업데이트 (병합하지 않음)
      setTimeTags(prev => prev.map(tag => {
        if (tag.id === editingTag.id) {
          return {
            ...tag,
            name: newTagName.trim(),
            startTime: startTimeToSave,
            endTime: newEndTime,
            dayOfWeek: selectedDays,
            updatedAt: new Date()
          };
        }
        return tag;
      }));

      setEditingTag(null);
    } else {
      // 새 태그 추가 - 같은 이름/시간의 블럭이 있는지 확인
      const existingTag = timeTags.find(tag =>
        tag.name === newTagName.trim() &&
        tag.startTime === startTimeToSave &&
        tag.endTime === newEndTime
      );

      if (existingTag) {
        // 기존 블럭에 요일 추가 (중복 제거)
        const mergedDays = Array.from(new Set([...existingTag.dayOfWeek, ...selectedDays]));
        setTimeTags(prev => prev.map(tag =>
          tag.id === existingTag.id
            ? { ...tag, dayOfWeek: mergedDays, updatedAt: new Date() }
            : tag
        ));
      } else {
        // 새 블럭 생성
        const newTag: TimeTag = {
          id: `TI_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: newTagName.trim(),
          startTime: startTimeToSave,
          endTime: newEndTime,
          dayOfWeek: selectedDays,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setTimeTags(prev => [...prev, newTag]);
      }
    }

    // 입력 필드 초기화
    setNewTagName('');
    setNewStartTime('');
    setNewDuration('');
    setNewEndTime('');
    setSelectedDays([]);
    setStartHourInput('');
    setStartMinuteInput('');
    setEndHourInput('');
    setEndMinuteInput('');
    setIsStartAm(true);
    setIsEndAm(true);
    setIsDurationDisabled(false);
    setIsEndTimeDisabled(false);
    setActiveTimeInput(null);
    setIsAddingNew(false);
  };

  const handleEditTag = (tag: TimeTag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setNewStartTime(tag.startTime);
    setNewEndTime(tag.endTime);
    setSelectedDays(Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek]);

    // 시간을 12시간 형식으로 변환하여 입력 필드에 설정
    const [startHour, startMinute] = tag.startTime.split(':').map(Number);
    const [endHour, endMinute] = tag.endTime.split(':').map(Number);

    const startIsAm = startHour < 12;
    const endIsAm = endHour < 12;

    setIsStartAm(startIsAm);
    setIsEndAm(endIsAm);

    setStartHourInput(startIsAm ? (startHour === 0 ? '12' : startHour.toString()) : (startHour === 12 ? '12' : (startHour - 12).toString()));
    setStartMinuteInput(startMinute.toString().padStart(2, '0'));
    setEndHourInput(endIsAm ? (endHour === 0 ? '12' : endHour.toString()) : (endHour === 12 ? '12' : (endHour - 12).toString()));
    setEndMinuteInput(endMinute.toString().padStart(2, '0'));
    updateDurationFromEndTime(tag.startTime, tag.endTime);
    setActiveTimeInput('endTime');
    setIsDurationDisabled(true);
    setIsEndTimeDisabled(false);

    setIsAddingNew(true);
  };

  const handleDeleteTag = (id: string, day?: DayOfWeek) => {
    setTagToDelete(id);
    setDayToDelete(day || null);
    setIsDeleteConfirmOpen(true);
  };

  const executeDeleteTag = () => {
    if (tagToDelete) {
      setTimeTags(prev => {
        const updatedTags = prev.map(tag => {
          if (tag.id === tagToDelete) {
            if (dayToDelete) {
              // 특정 요일만 제거
              const updatedDays = tag.dayOfWeek.filter(day => day !== dayToDelete);
              if (updatedDays.length === 0) {
                // 모든 요일이 제거되면 null 반환하여 삭제
                return null;
              }
              return { ...tag, dayOfWeek: updatedDays, updatedAt: new Date() };
            } else {
              // 전체 삭제
              return null;
            }
          }
          return tag;
        }).filter(tag => tag !== null) as TimeTag[];
        return updatedTags;
      });
      setIsDeleteConfirmOpen(false);
      setTagToDelete(null);
      setDayToDelete(null);
    }
  };

  // 다른 클래스 데이터 가져오기 모달 열기
  const handleOpenImportModal = async () => {
    if (!userId || !classId) return;
    
    setShowImportModal(true);
    setSelectedSourceClassId('');
    setSourceTimeTags([]);
    setSelectedTimeTagIds(new Set());
    
    try {
      const allClasses = await userDataService.loadUserClasses(userId);
      const filteredClasses = allClasses.filter(c => c.id !== classId);
      setAvailableClasses(filteredClasses);
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      setAvailableClasses([]);
    }
  };

  // 다른 클래스 데이터 가져오기 모달 닫기
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setSelectedSourceClassId('');
    setSourceTimeTags([]);
    setSelectedTimeTagIds(new Set());
    setIsClassDropdownOpen(false);
  };

  // 소스 클래스 변경 핸들러
  const handleSourceClassChange = async (sourceClassId: string) => {
    if (!userId) return;
    
    setSelectedSourceClassId(sourceClassId);
    setIsClassDropdownOpen(false);
    setIsLoadingSourceTimeTags(true);
    
    try {
      const sourceTags = await TimeTagService.getTimeTags(userId, sourceClassId);
      setSourceTimeTags(sourceTags);
      setSelectedTimeTagIds(new Set());
    } catch (error) {
      console.error('소스 클래스 타임태그 로드 실패:', error);
      setSourceTimeTags([]);
    } finally {
      setIsLoadingSourceTimeTags(false);
    }
  };

  // 타임태그 선택 토글
  const handleToggleTimeTagSelection = (tagId: string) => {
    setSelectedTimeTagIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  // 선택한 타임태그 적용
  const handleApplySelectedTimeTags = () => {
    if (selectedTimeTagIds.size === 0) return;
    
    const selectedTags = sourceTimeTags.filter(tag => 
      selectedTimeTagIds.has(tag.id)
    );
    
    // 중복 체크 (이름, 시작시간, 종료시간, 요일 조합으로)
    const existingCombinations = new Set(
      timeTags.map(tag => 
        `${tag.name}_${tag.startTime}_${tag.endTime}_${Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek.sort().join(',') : tag.dayOfWeek}`
      )
    );
    
    const tagsToAdd = selectedTags.filter(tag => {
      const combination = `${tag.name}_${tag.startTime}_${tag.endTime}_${Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek.sort().join(',') : tag.dayOfWeek}`;
      return !existingCombinations.has(combination);
    });
    
    if (tagsToAdd.length === 0) {
      alert('모든 선택한 타임태그가 이미 존재합니다.');
      return;
    }
    
    // 새 ID 생성하여 추가
    const newTags = tagsToAdd.map(tag => ({
      ...tag,
      id: `TI_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date()
    }));
    
    setTimeTags(prev => [...prev, ...newTags]);
    handleCloseImportModal();
    alert(`${tagsToAdd.length}개의 타임태그가 추가되었습니다.`);
  };

  const handleSave = async () => {
    if (isSaving) return; // 이미 저장 중이면 중복 실행 방지

    try {
      setIsSaving(true);
      onSave(timeTags);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  // 알람 만들기 버튼 클릭 핸들러
  const handleCreateAlarms = () => {
    if (selectedCreateDays.length === 0) {
      alert('요일을 선택해주세요.');
      return;
    }
    if (alarmSets.length === 0) {
      alert('알람 세트가 없습니다.');
      return;
    }
    setShowCreateAlarmConfirm(true);
  };

  // 알람 생성 실행
  const executeCreateAlarms = async () => {
    if (!onCreateAlarms || alarmSets.length === 0 || selectedCreateDays.length === 0) {
      setShowCreateAlarmConfirm(false);
      return;
    }

    const alarmsToCreate: { alarm: Alarm; setIndex: number; dayIndex: number }[] = [];

    // 각 선택한 요일에 대해 알람 생성
    for (const dayIndex of selectedCreateDays) {
      const dayName = getDayName(dayIndex);
      
      // 해당 요일의 타임 태그 찾기 (dayOfWeek 배열에 해당 요일이 포함된 태그)
      let timeTagsForDay: TimeTag[] = timeTags.filter(tag => {
        const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
        return days.includes(dayName as DayOfWeek);
      });

      // 찾은 타임 태그들에 대해 각각 알람 생성
      for (const timeTagForDay of timeTagsForDay) {
        const newAlarm: Alarm = {
          id: Date.now().toString() + '-' + dayIndex + '-' + timeTagForDay.id,
          time: timeTagForDay.startTime,
          type: '3M', // 기본 3분 전 알람
          name: timeTagForDay.name,
          days: [dayIndex],
          isActive: true,
          isExpanded: false
        };

        alarmsToCreate.push({
          alarm: newAlarm,
          setIndex: selectedAlarmSetIndex,
          dayIndex: dayIndex
        });
      }
    }

    if (alarmsToCreate.length > 0) {
      try {
        await onCreateAlarms(alarmsToCreate);
        alert(`알람이 성공적으로 생성되었습니다. (${alarmsToCreate.length}개)`);
        setIsCreateAlarmModalOpen(false);
        setSelectedCreateDays([]);
      } catch (error) {
        console.error('알람 생성 실패:', error);
        alert('알람 생성 중 오류가 발생했습니다.');
      }
    } else {
      alert('생성할 알람이 없습니다. 타임 태그를 확인해주세요.');
    }

    setShowCreateAlarmConfirm(false);
  };

  const handleCancelAdd = () => {
    setNewTagName('');
    setNewStartTime('');
    setNewDuration('');
    setNewEndTime('');
    setSelectedDays([]); // 요일 선택 초기화 (아무것도 선택되지 않음)
    setStartHourInput('');
    setStartMinuteInput('');
    setEndHourInput('');
    setEndMinuteInput('');
    setIsStartAm(true);
    setIsEndAm(true);
    setIsDurationDisabled(false);
    setIsEndTimeDisabled(false);
    setActiveTimeInput(null);
    setEditingTag(null);
    setIsAddingNew(false);
  };

  // + 버튼 클릭 핸들러
  const handleAddNewClick = () => {
    // 현재 탭에 따라 요일 선택 초기화
    if (activeTab === 'all') {
      // 전체 탭에서는 아무것도 선택하지 않음
      setSelectedDays([]);
    } else {
      // 특정 탭에서는 해당 요일을 기본 선택
      setSelectedDays([activeTab]);
    }
    
    // 폼 초기화
    setNewTagName('');
    setNewStartTime('');
    setNewEndTime('');
    setNewDuration('');
    setStartHourInput('');
    setStartMinuteInput('');
    setEndHourInput('');
    setEndMinuteInput('');
    setIsStartAm(true);
    setIsEndAm(true);
    setIsDurationDisabled(false);
    setIsEndTimeDisabled(false);
    setActiveTimeInput(null);
    setEditingTag(null);
    
    // 모달 열기
    setIsAddingNew(true);
  };

  const formatTime = (time: string) => {
    return time || '00:00';
  };

  // 타임 태그 필터링 및 정렬 함수
  const getFilteredTimeTags = () => {
    let filteredTags = activeTab === 'all' 
      ? [...timeTags]
      : timeTags.filter(tag => {
          const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
          return days.includes(activeTab);
        });
    
    // 시작 시각에 따라 정렬, 시작 시각이 같으면 종료 시각이 빠른 것이 앞
    return filteredTags.sort((a, b) => {
      // 시작 시각 비교
      const startCompare = a.startTime.localeCompare(b.startTime);
      if (startCompare !== 0) return startCompare;
      
      // 시작 시각이 같으면 종료 시각 비교 (빠른 것이 앞)
      return a.endTime.localeCompare(b.endTime);
    });
  };

  if (!isOpen && !renderAsSection) return null;

  const bodyContent = (
    <>
          <Description>
            시간대별로 태그를 설정하여 해당 시간에 기록된 활동을 분류할 수 있습니다.
          </Description>

          {/* 타임 태그 탭 */}
          <TimeTagTabsContainer>
            {/* 전체 탭 - 1행 */}
            <TimeTagTabRow>
              <TimeTagTab
                $active={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
                $fullWidth={true}
              >
                전체
              </TimeTagTab>
            </TimeTagTabRow>
            
            {/* 요일 탭들 - 2행 */}
            <TimeTagTabRow>
              {(['일', '월', '화', '수', '목', '금', '토'] as const).map(day => (
                <TimeTagTab
                  key={day}
                  $active={activeTab === day}
                  onClick={() => setActiveTab(day)}
                >
                  {day}
                </TimeTagTab>
              ))}
            </TimeTagTabRow>
          </TimeTagTabsContainer>

          {/* 기존 태그 목록 */}
          <TagBlocksContainer>
            {getFilteredTimeTags().map((tag) => {
              const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
              // 요일 순서 정의: 일, 월, 화, 수, 목, 금, 토
              const dayOrder: DayOfWeek[] = ['일', '월', '화', '수', '목', '금', '토'];
              // 전체 탭에서는 정렬된 요일 표시, 특정 요일 탭에서는 해당 요일만 표시
              const displayDays = activeTab === 'all' 
                ? days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b)).join(', ')
                : activeTab;
              return (
                <TagBlock key={tag.id}>
                  <TagBlockContent>
                    <TagLeftGroup>
                      <TagDayOfWeek>{displayDays}</TagDayOfWeek>
                      <TagName>{tag.name}</TagName>
                    </TagLeftGroup>
                    <TagRightGroup>
                      <TagTimeRange>
                        {formatTime(tag.startTime)} ~ {formatTime(tag.endTime)}
                      </TagTimeRange>
                      <ActionButtons>
                        <EditButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTag(tag);
                          }}
                          title="수정"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </EditButton>
                        <DeleteButton
                          onClick={(e) => {
                            e.stopPropagation();
                            // activeTab이 특정 요일이면 해당 요일만 제거, 'all'이면 전체 삭제
                            const dayToRemove = activeTab !== 'all' ? (activeTab as DayOfWeek) : undefined;
                            handleDeleteTag(tag.id, dayToRemove);
                          }}
                          title={activeTab !== 'all' ? `${activeTab}에서 제거` : '전체 삭제'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </DeleteButton>
                      </ActionButtons>
                    </TagRightGroup>
                  </TagBlockContent>
                </TagBlock>
              );
            })}

            {/* 새 태그 추가 영역 - 제거됨, 별도 모달로 분리 */}
            {false && (
              <NewTagBlock>
                <NewTagContent>
                  <InputRow>
                    <InputGroup>
                      <Label>태그 이름</Label>
                      <Input
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="예: 수업시간, 쉬는시간"
                      />
                    </InputGroup>
                  </InputRow>

                  {/* 요일 선택 버튼들 */}
                  <DayOfWeekSection>
                    <DayOfWeekLabel>요일 선택</DayOfWeekLabel>
                    <DayOfWeekRow>
                      <AllDaysButton
                        selected={selectedDays.length === 7}
                        onClick={toggleAllDays}
                      >
                        전체 요일
                      </AllDaysButton>
                    </DayOfWeekRow>
                    <DayOfWeekButtons>
                      {(['일', '월', '화', '수', '목', '금', '토'] as DayOfWeek[]).map(day => (
                        <DayOfWeekButton
                          key={day}
                          selected={selectedDays.includes(day)}
                          onClick={() => toggleDay(day)}
                        >
                          {day}
                        </DayOfWeekButton>
                      ))}
                    </DayOfWeekButtons>
                  </DayOfWeekSection>

                  <TimeInputSection>
                    <TimeInputLabel>시작 시간</TimeInputLabel>
                    <TimeInputContainer>
                      <TimeGroup>
                        <AmPmToggle isAm={isStartAm} onClick={handleToggleStartAm}>
                          {isStartAm ? '오전' : '오후'}
                        </AmPmToggle>
                        <HourInput
                          type="text"
                          value={startHourInput}
                          onChange={handleStartHourChange}
                          onBlur={handleStartHourBlur}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                        />
                        <TimeSeparator>:</TimeSeparator>
                        <MinuteInput
                          type="text"
                          value={startMinuteInput}
                          onChange={handleStartMinuteChange}
                          onBlur={handleStartMinuteBlur}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                        />
                      </TimeGroup>
                      {startHourInput && (
                        <ResetButton onClick={handleResetStartTime} title="시작 시간 초기화">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </ResetButton>
                      )}
                    </TimeInputContainer>

                    <TimeInputLabel>활동 시간 (분)</TimeInputLabel>
                    <DurationInputContainer>
                      <DurationInput
                        type="number"
                        value={newDuration}
                        onChange={handleDurationChange}
                        placeholder="분"
                        min="0"
                        $disabled={isDurationDisabled}
                        disabled={isDurationDisabled}
                      />
                      {newDuration && !isDurationDisabled && (
                        <ResetButton onClick={handleResetDuration} title="활동 시간 초기화">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </ResetButton>
                      )}
                    </DurationInputContainer>

                    <TimeInputLabel>종료 시간</TimeInputLabel>
                    <TimeInputContainer>
                      <TimeGroup>
                        <AmPmToggle isAm={isEndAm} onClick={handleToggleEndAm}>
                          {isEndAm ? '오전' : '오후'}
                        </AmPmToggle>
                        <HourInput
                          type="text"
                          value={endHourInput}
                          onChange={handleEndHourChange}
                          onBlur={handleEndHourBlur}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                          disabled={isEndTimeDisabled}
                        />
                        <TimeSeparator>:</TimeSeparator>
                        <MinuteInput
                          type="text"
                          value={endMinuteInput}
                          onChange={handleEndMinuteChange}
                          onBlur={handleEndMinuteBlur}
                          onFocus={(e) => e.target.select()}
                          maxLength={2}
                          placeholder="00"
                          disabled={isEndTimeDisabled}
                        />
                      </TimeGroup>
                      {endHourInput && !isEndTimeDisabled && (
                        <ResetButton onClick={handleResetEndTime} title="종료 시간 초기화">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </ResetButton>
                      )}
                    </TimeInputContainer>
                  </TimeInputSection>

                  <ButtonRow>
                    <CancelAddButton data-variant="modal-secondary" onClick={handleCancelAdd}>
                      취소
                    </CancelAddButton>
                    <AddTagButton
                      onClick={handleAddTag}
                    disabled={!newTagName.trim() || selectedDays.length === 0 || !newEndTime}
                    >
                      추가
                    </AddTagButton>
                  </ButtonRow>
                </NewTagContent>
              </NewTagBlock>
            )}

            {/* 원형 + 버튼 */}
            <AddButtonContainer>
              <AddButton onClick={handleAddNewClick}>
                <PlusIcon>+</PlusIcon>
              </AddButton>
            </AddButtonContainer>
            
            {/* 다른 클래스 데이터 가져오기 버튼 */}
            {userId && classId && isImportButtonEnabled && (
              <ImportButton onClick={handleOpenImportModal}>
                다른 클래스 데이터 가져오기
              </ImportButton>
            )}
          </TagBlocksContainer>
    </>
  );

  if (renderAsSection) {
    return (
      <>
        <SectionWrapper>
          <SectionContent>{bodyContent}</SectionContent>
          <CreateAlarmButton onClick={() => setIsCreateAlarmModalOpen(true)}>
            타임 태그 일정으로 알람 만들기
          </CreateAlarmButton>
        </SectionWrapper>

      {/* 삭제 확인 모달 */}
      {isDeleteConfirmOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={isDeleteConfirmOpen}
            onClose={() => {
              setIsDeleteConfirmOpen(false);
              setTagToDelete(null);
              setDayToDelete(null);
            }}
            onConfirm={executeDeleteTag}
            title={dayToDelete 
              ? [
                  `이 타임 태그를 ${dayToDelete}에서 제거할까요?`,
                  "해당 요일에서만 제거되며, 다른 요일에는 계속 적용됩니다."
                ]
              : [
                  "이 타임 태그를 삭제할까요?",
                  "모든 요일에서 제거됩니다."
                ]
            }
            confirmText={dayToDelete ? "제거" : "삭제"}
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 타임 태그 생성 모달 */}
      {isAddingNew && (
        <CreateTagModalOverlay onClick={handleCancelAdd}>
          <CreateTagModalContent onClick={(e) => e.stopPropagation()}>
            <CreateTagModalHeader>
              <CreateTagModalTitle>
                {editingTag ? '타임 태그 수정' : '새 타임 태그 추가'}
              </CreateTagModalTitle>
              <CreateTagCloseButton onClick={handleCancelAdd}>×</CreateTagCloseButton>
            </CreateTagModalHeader>

            <CreateTagModalBody>
              <InputRow>
                <InputGroup>
                  <Label>태그 이름</Label>
                  <InputWithCounter>
                    <Input
                      type="text"
                      value={newTagName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 6) {
                          setNewTagName(value);
                        }
                      }}
                      placeholder="예: 수업시간, 쉬는시간"
                      maxLength={6}
                    />
                    <CharCounter>
                      {newTagName.length}/6
                    </CharCounter>
                  </InputWithCounter>
                </InputGroup>
              </InputRow>

              {/* 요일 선택 버튼들 */}
              <DayOfWeekSection>
                <DayOfWeekLabel>요일 선택</DayOfWeekLabel>
                <DayOfWeekRow>
                  <AllDaysButton
                    selected={selectedDays.length === 7}
                    onClick={toggleAllDays}
                  >
                    전체 요일
                  </AllDaysButton>
                </DayOfWeekRow>
                <DayOfWeekButtons>
                  {(['일', '월', '화', '수', '목', '금', '토'] as DayOfWeek[]).map(day => (
                    <DayOfWeekButton
                      key={day}
                      selected={selectedDays.includes(day)}
                      onClick={() => toggleDay(day)}
                    >
                      {day}
                    </DayOfWeekButton>
                  ))}
                </DayOfWeekButtons>
              </DayOfWeekSection>

              <TimeInputSection>
                <TimeInputLabel>시작 시간</TimeInputLabel>
                <TimeInputContainer>
                  <TimeGroup>
                    <AmPmToggle isAm={isStartAm} onClick={handleToggleStartAm}>
                      {isStartAm ? '오전' : '오후'}
                    </AmPmToggle>
                    <HourInput
                      type="text"
                      value={startHourInput}
                      onChange={handleStartHourChange}
                      onBlur={handleStartHourBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <MinuteInput
                      type="text"
                      value={startMinuteInput}
                      onChange={handleStartMinuteChange}
                      onBlur={handleStartMinuteBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                    />
                  </TimeGroup>
                  {startHourInput && (
                    <ResetButton onClick={handleResetStartTime} title="시작 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </TimeInputContainer>

                <TimeInputLabel>활동 시간 (분)</TimeInputLabel>
                <DurationInputContainer>
                  <DurationInput
                    type="number"
                    value={newDuration}
                    onChange={handleDurationChange}
                    placeholder="분"
                    min="0"
                    $disabled={isDurationDisabled}
                    disabled={isDurationDisabled}
                  />
                  {newDuration && !isDurationDisabled && (
                    <ResetButton onClick={handleResetDuration} title="활동 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </DurationInputContainer>

                <TimeInputLabel>종료 시간</TimeInputLabel>
                <TimeInputContainer>
                  <TimeGroup>
                    <AmPmToggle isAm={isEndAm} onClick={handleToggleEndAm}>
                      {isEndAm ? '오전' : '오후'}
                    </AmPmToggle>
                    <HourInput
                      type="text"
                      value={endHourInput}
                      onChange={handleEndHourChange}
                      onBlur={handleEndHourBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                      disabled={isEndTimeDisabled}
                      $disabled={isEndTimeDisabled}
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <MinuteInput
                      type="text"
                      value={endMinuteInput}
                      onChange={handleEndMinuteChange}
                      onBlur={handleEndMinuteBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                      disabled={isEndTimeDisabled}
                      $disabled={isEndTimeDisabled}
                    />
                  </TimeGroup>
                  {endHourInput && (
                    <ResetButton onClick={handleResetEndTime} title="종료 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </TimeInputContainer>
              </TimeInputSection>
            </CreateTagModalBody>

            <CreateTagModalFooter>
              <CancelAddButton data-variant="modal-secondary" onClick={handleCancelAdd}>
                취소
              </CancelAddButton>
              <AddTagButton
                onClick={handleAddTag}
                disabled={!newTagName.trim() || selectedDays.length === 0 || !newEndTime}
              >
                {editingTag ? '수정' : '추가'}
              </AddTagButton>
            </CreateTagModalFooter>
          </CreateTagModalContent>
        </CreateTagModalOverlay>
      )}

      {/* 알람 만들기 모달 */}
      {isCreateAlarmModalOpen && (
        <CreateAlarmModalOverlay onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setIsCreateAlarmModalOpen(false);
        }}>
          <CreateAlarmModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CreateAlarmModalHeader>
              <CreateAlarmModalTitle>타임 태그 일정으로 알람 만들기</CreateAlarmModalTitle>
              <CreateAlarmCloseButton onClick={() => setIsCreateAlarmModalOpen(false)}>×</CreateAlarmCloseButton>
            </CreateAlarmModalHeader>

            <CreateAlarmModalBody>
              <InfoMessage>
                선택한 요일의 타임 태그의 시작 시각을 기준으로 해당 요일의 알람이 생성됩니다. 전체 요일을 선택한 타임 태그는 해당 요일에도 적용됩니다. 기존 알람이 있는 경우 기존 알람이 삭제되고 새로 생성되니 이미 알람 설정이 있는 경우 주의하시기 바랍니다.
              </InfoMessage>

              <AlarmSetSelector>
                <Label>알람 세트 선택</Label>
                <AlarmSetSelect
                  value={selectedAlarmSetIndex}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedAlarmSetIndex(Number(e.target.value))}
                >
                  {alarmSets.map((set, index) => (
                    <option key={set.id} value={index}>
                      {set.name}
                    </option>
                  ))}
                </AlarmSetSelect>
              </AlarmSetSelector>

              <DaySelectorContainer>
                <Label>요일 선택</Label>
                <DaySelectorGrid>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <DaySelectButton
                      key={dayIndex}
                      $isSelected={selectedCreateDays.includes(dayIndex)}
                      onClick={() => {
                        setSelectedCreateDays(prev =>
                          prev.includes(dayIndex)
                            ? prev.filter(d => d !== dayIndex)
                            : [...prev, dayIndex].sort()
                        );
                      }}
                    >
                      {getDayName(dayIndex)}
                    </DaySelectButton>
                  ))}
                </DaySelectorGrid>
              </DaySelectorContainer>
            </CreateAlarmModalBody>

            <CreateAlarmModalFooter>
              <CancelAddButton onClick={() => setIsCreateAlarmModalOpen(false)}>
                취소
              </CancelAddButton>
              <AddTagButton
                onClick={handleCreateAlarms}
                disabled={selectedCreateDays.length === 0 || alarmSets.length === 0}
              >
                만들기
              </AddTagButton>
            </CreateAlarmModalFooter>
          </CreateAlarmModalContent>
        </CreateAlarmModalOverlay>
      )}

      {/* 알람 생성 확인 모달 */}
      {showCreateAlarmConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCreateAlarmConfirm}
            onClose={() => setShowCreateAlarmConfirm(false)}
            onConfirm={executeCreateAlarms}
            title={[
              "알람을 생성하시겠습니까?",
              "선택한 요일의 기존 알람이 삭제됩니다."
            ]}
            confirmText="생성"
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 다른 클래스 데이터 가져오기 모달 */}
      {showImportModal && (
        <ImportModalOverlay onClick={handleCloseImportModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>다른 클래스 데이터 가져오기</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseImportModal}>×</ImportModalCloseButton>
            </ImportModalHeader>
            
            <ImportModalBody>
              {/* 클래스 선택 드롭다운 */}
              <ImportSection>
                <ImportLabel>클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {selectedSourceClassId 
                        ? availableClasses.find(c => c.id === selectedSourceClassId)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSelectedSourceClassId('');
                            setIsClassDropdownOpen(false);
                            setSourceTimeTags([]);
                            setSelectedTimeTagIds(new Set());
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {availableClasses.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => handleSourceClassChange(cls.id)}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>
              
              {/* 타임태그 목록 */}
              {isLoadingSourceTimeTags ? (
                <ImportLoadingText>타임태그를 불러오는 중...</ImportLoadingText>
              ) : selectedSourceClassId && sourceTimeTags.length === 0 ? (
                <ImportEmptyText>선택한 클래스에 타임태그가 없습니다.</ImportEmptyText>
              ) : selectedSourceClassId && sourceTimeTags.length > 0 ? (
                <ImportSection>
                  <ImportLabel>타임태그 선택</ImportLabel>
                  <ImportTimeTagGrid>
                    {sourceTimeTags.map(tag => {
                      const isSelected = selectedTimeTagIds.has(tag.id);
                      const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
                      const dayOrder: DayOfWeek[] = ['일', '월', '화', '수', '목', '금', '토'];
                      const sortedDays = days.filter(d => dayOrder.includes(d)).sort((a, b) => 
                        dayOrder.indexOf(a) - dayOrder.indexOf(b)
                      );
                      const displayDays = sortedDays.join(', ');
                      
                      return (
                        <ImportTimeTagBlock
                          key={tag.id}
                          $isSelected={isSelected}
                          onClick={() => handleToggleTimeTagSelection(tag.id)}
                        >
                          <ImportTimeTagContent>
                            <ImportTagLeftGroup>
                              <ImportTimeTagDayOfWeek>{displayDays}</ImportTimeTagDayOfWeek>
                              <ImportTimeTagName>{tag.name}</ImportTimeTagName>
                            </ImportTagLeftGroup>
                            <ImportTimeTagTimeRange>
                              {formatTime(tag.startTime)} ~ {formatTime(tag.endTime)}
                            </ImportTimeTagTimeRange>
                          </ImportTimeTagContent>
                          {isSelected && (
                            <ImportCheckIcon>✓</ImportCheckIcon>
                          )}
                        </ImportTimeTagBlock>
                      );
                    })}
                  </ImportTimeTagGrid>
                </ImportSection>
              ) : null}
            </ImportModalBody>
            
            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseImportModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleApplySelectedTimeTags}
                $variant="primary"
                disabled={selectedTimeTagIds.size === 0}
              >
                <span className="button-text-full">현재 클래스에 반영</span>
                <span className="button-text-short">클래스에 반영</span>
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}

      {/* 취소 확인 모달 */}
      {showCancelConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              onClose();
            }}
            title={[
              "변경사항이 있습니다.",
              "저장하지 않고 나가시겠습니까?",
              "변경사항이 모두 사라집니다."
            ]}
            confirmText="나가기"
            cancelText="저장"
            icon="!"
            iconBackground="var(--status-exempted)"
            onCancel={() => {
              setShowCancelConfirm(false);
              handleSave();
            }}
          />
        </div>
      )}
    </>
  );
}

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>타임 태그 설정</ModalTitle>
          <CloseButton onClick={handleCancel}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {bodyContent}
        </ModalBody>

        <ModalFooter>
          <CreateAlarmButton onClick={() => setIsCreateAlarmModalOpen(true)}>
            타임 태그 일정으로 알람 만들기
          </CreateAlarmButton>
          <CancelButton
            data-variant="modal-secondary"
            onClick={handleCancel}
          >
            닫기
          </CancelButton>
          <SaveButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장중...' : '저장'}
          </SaveButton>
        </ModalFooter>
      </ModalContent>

      {/* 삭제 확인 모달 */}
      {isDeleteConfirmOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={isDeleteConfirmOpen}
            onClose={() => {
              setIsDeleteConfirmOpen(false);
              setTagToDelete(null);
              setDayToDelete(null);
            }}
            onConfirm={executeDeleteTag}
            title={dayToDelete
              ? [
                  `이 타임 태그를 ${dayToDelete}에서 제거할까요?`,
                  "해당 요일에서만 제거되며, 다른 요일에는 계속 적용됩니다."
                ]
              : [
                  "이 타임 태그를 삭제할까요?",
                  "모든 요일에서 제거됩니다."
                ]
            }
            confirmText={dayToDelete ? "제거" : "삭제"}
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 타임 태그 생성 모달 */}
      {isAddingNew && (
        <CreateTagModalOverlay onClick={handleCancelAdd}>
          <CreateTagModalContent onClick={(e) => e.stopPropagation()}>
            <CreateTagModalHeader>
              <CreateTagModalTitle>
                {editingTag ? '타임 태그 수정' : '새 타임 태그 추가'}
              </CreateTagModalTitle>
              <CreateTagCloseButton onClick={handleCancelAdd}>×</CreateTagCloseButton>
            </CreateTagModalHeader>

            <CreateTagModalBody>
              <InputRow>
                <InputGroup>
                  <Label>태그 이름</Label>
                  <InputWithCounter>
                    <Input
                      type="text"
                      value={newTagName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 6) {
                          setNewTagName(value);
                        }
                      }}
                      placeholder="예: 수업시간, 쉬는시간"
                      maxLength={6}
                    />
                    <CharCounter>
                      {newTagName.length}/6
                    </CharCounter>
                  </InputWithCounter>
                </InputGroup>
              </InputRow>

              <DayOfWeekSection>
                <DayOfWeekLabel>요일 선택</DayOfWeekLabel>
                <DayOfWeekRow>
                  <AllDaysButton
                    selected={selectedDays.length === 7}
                    onClick={toggleAllDays}
                  >
                    전체 요일
                  </AllDaysButton>
                </DayOfWeekRow>
                <DayOfWeekButtons>
                  {(['일', '월', '화', '수', '목', '금', '토'] as DayOfWeek[]).map(day => (
                    <DayOfWeekButton
                      key={day}
                      selected={selectedDays.includes(day)}
                      onClick={() => toggleDay(day)}
                    >
                      {day}
                    </DayOfWeekButton>
                  ))}
                </DayOfWeekButtons>
              </DayOfWeekSection>

              <TimeInputSection>
                <TimeInputLabel>시작 시간</TimeInputLabel>
                <TimeInputContainer>
                  <TimeGroup>
                    <AmPmToggle isAm={isStartAm} onClick={handleToggleStartAm}>
                      {isStartAm ? '오전' : '오후'}
                    </AmPmToggle>
                    <HourInput
                      type="text"
                      value={startHourInput}
                      onChange={handleStartHourChange}
                      onBlur={handleStartHourBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <MinuteInput
                      type="text"
                      value={startMinuteInput}
                      onChange={handleStartMinuteChange}
                      onBlur={handleStartMinuteBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                    />
                  </TimeGroup>
                  {startHourInput && (
                    <ResetButton onClick={handleResetStartTime} title="시작 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </TimeInputContainer>

                <TimeInputLabel>활동 시간 (분)</TimeInputLabel>
                <DurationInputContainer>
                  <DurationInput
                    type="number"
                    value={newDuration}
                    onChange={handleDurationChange}
                    min={1}
                    max={1440}
                    disabled={isDurationDisabled}
                  />
                  {newDuration && !isDurationDisabled && (
                    <ResetButton onClick={handleResetDuration} title="활동 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </DurationInputContainer>

                <TimeInputLabel>종료 시간</TimeInputLabel>
                <TimeInputContainer>
                  <TimeGroup>
                    <AmPmToggle isAm={isEndAm} onClick={handleToggleEndAm}>
                      {isEndAm ? '오전' : '오후'}
                    </AmPmToggle>
                    <HourInput
                      type="text"
                      value={endHourInput}
                      onChange={handleEndHourChange}
                      onBlur={handleEndHourBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                      $disabled={isEndTimeDisabled}
                    />
                    <TimeSeparator>:</TimeSeparator>
                    <MinuteInput
                      type="text"
                      value={endMinuteInput}
                      onChange={handleEndMinuteChange}
                      onBlur={handleEndMinuteBlur}
                      onFocus={(e) => e.target.select()}
                      maxLength={2}
                      placeholder="00"
                      $disabled={isEndTimeDisabled}
                    />
                  </TimeGroup>
                  {endHourInput && (
                    <ResetButton onClick={handleResetEndTime} title="종료 시간 초기화">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ResetButton>
                  )}
                </TimeInputContainer>
              </TimeInputSection>

              <CreateTagModalFooter>
                <CancelAddButton data-variant="modal-secondary" onClick={handleCancelAdd}>
                  취소
                </CancelAddButton>
                <AddTagButton
                  onClick={handleAddTag}
                  disabled={!newTagName.trim() || selectedDays.length === 0 || !newEndTime}
                >
                  {editingTag ? '수정' : '추가'}
                </AddTagButton>
              </CreateTagModalFooter>
            </CreateTagModalBody>
          </CreateTagModalContent>
        </CreateTagModalOverlay>
      )}

      {/* 알람 만들기 모달 */}
      {isCreateAlarmModalOpen && (
        <CreateAlarmModalOverlay onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setIsCreateAlarmModalOpen(false);
        }}>
          <CreateAlarmModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CreateAlarmModalHeader>
              <CreateAlarmModalTitle>타임 태그 일정으로 알람 만들기</CreateAlarmModalTitle>
              <CreateAlarmCloseButton onClick={() => setIsCreateAlarmModalOpen(false)}>×</CreateAlarmCloseButton>
            </CreateAlarmModalHeader>

            <CreateAlarmModalBody>
              <InfoMessage>
                선택한 요일의 타임 태그의 시작 시각을 기준으로 해당 요일의 알람이 생성됩니다. 전체 요일을 선택한 타임 태그는 해당 요일에도 적용됩니다. 기존 알람이 있는 경우 기존 알람이 삭제되고 새로 생성되니 이미 알람 설정이 있는 경우 주의하시기 바랍니다.
              </InfoMessage>

              <AlarmSetSelector>
                <Label>알람 세트 선택</Label>
                <AlarmSetSelect
                  value={selectedAlarmSetIndex}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedAlarmSetIndex(Number(e.target.value))}
                >
                  {alarmSets.map((set, idx) => (
                    <option key={set.id} value={idx}>
                      {set.name}
                    </option>
                  ))}
                </AlarmSetSelect>
              </AlarmSetSelector>

              <DaySelectorContainer>
                <Label>요일 선택</Label>
                <DaySelectorGrid>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                    <DaySelectButton
                      key={dayIndex}
                      $isSelected={selectedCreateDays.includes(dayIndex)}
                      onClick={() => {
                        setSelectedCreateDays(prev =>
                          prev.includes(dayIndex)
                            ? prev.filter(d => d !== dayIndex)
                            : [...prev, dayIndex].sort()
                        );
                      }}
                    >
                      {getDayName(dayIndex)}
                    </DaySelectButton>
                  ))}
                </DaySelectorGrid>
              </DaySelectorContainer>
            </CreateAlarmModalBody>

            <CreateAlarmModalFooter>
              <CancelAddButton onClick={() => setIsCreateAlarmModalOpen(false)}>
                취소
              </CancelAddButton>
              <AddTagButton
                onClick={handleCreateAlarms}
                disabled={selectedCreateDays.length === 0 || alarmSets.length === 0}
              >
                만들기
              </AddTagButton>
            </CreateAlarmModalFooter>
          </CreateAlarmModalContent>
        </CreateAlarmModalOverlay>
      )}

      {/* 알람 생성 확인 모달 */}
      {showCreateAlarmConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCreateAlarmConfirm}
            onClose={() => setShowCreateAlarmConfirm(false)}
            onConfirm={executeCreateAlarms}
            title={[
              "알람을 생성하시겠습니까?",
              "선택한 요일의 기존 알람이 삭제됩니다."
            ]}
            confirmText="생성"
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 다른 클래스 데이터 가져오기 모달 */}
      {showImportModal && (
        <ImportModalOverlay onClick={handleCloseImportModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>다른 클래스 데이터 가져오기</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseImportModal}>×</ImportModalCloseButton>
            </ImportModalHeader>

            <ImportModalBody>
              <ImportSection>
                <ImportLabel>클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {selectedSourceClassId
                        ? availableClasses.find(c => c.id === selectedSourceClassId)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSelectedSourceClassId('');
                            setIsClassDropdownOpen(false);
                            setSourceTimeTags([]);
                            setSelectedTimeTagIds(new Set());
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {availableClasses.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => handleSourceClassChange(cls.id)}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>

              {isLoadingSourceTimeTags ? (
                <ImportLoadingText>타임태그를 불러오는 중...</ImportLoadingText>
              ) : selectedSourceClassId && sourceTimeTags.length === 0 ? (
                <ImportEmptyText>선택한 클래스에 타임태그가 없습니다.</ImportEmptyText>
              ) : selectedSourceClassId && sourceTimeTags.length > 0 ? (
                <ImportSection>
                  <ImportLabel>타임태그 선택</ImportLabel>
                  <ImportTimeTagGrid>
                    {sourceTimeTags.map(tag => {
                      const isSelected = selectedTimeTagIds.has(tag.id);
                      const days = Array.isArray(tag.dayOfWeek) ? tag.dayOfWeek : [tag.dayOfWeek];
                      const dayOrder: DayOfWeek[] = ['일', '월', '화', '수', '목', '금', '토'];
                      const sortedDays = days.filter(d => dayOrder.includes(d)).sort((a, b) =>
                        dayOrder.indexOf(a) - dayOrder.indexOf(b)
                      );
                      const displayDays = sortedDays.join(', ');

                      return (
                        <ImportTimeTagBlock
                          key={tag.id}
                          $isSelected={isSelected}
                          onClick={() => handleToggleTimeTagSelection(tag.id)}
                        >
                          <ImportTimeTagContent>
                            <ImportTagLeftGroup>
                              <ImportTimeTagDayOfWeek>{displayDays}</ImportTimeTagDayOfWeek>
                              <ImportTimeTagName>{tag.name}</ImportTimeTagName>
                            </ImportTagLeftGroup>
                            <ImportTimeTagTimeRange>
                              {formatTime(tag.startTime)} ~ {formatTime(tag.endTime)}
                            </ImportTimeTagTimeRange>
                          </ImportTimeTagContent>
                          {isSelected && (
                            <ImportCheckIcon>✓</ImportCheckIcon>
                          )}
                        </ImportTimeTagBlock>
                      );
                    })}
                  </ImportTimeTagGrid>
                </ImportSection>
              ) : null}
            </ImportModalBody>

            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseImportModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleApplySelectedTimeTags}
                $variant="primary"
                disabled={selectedTimeTagIds.size === 0}
              >
                <span className="button-text-full">현재 클래스에 반영</span>
                <span className="button-text-short">클래스에 반영</span>
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}

      {/* 취소 확인 모달 */}
      {showCancelConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              onClose();
            }}
            title={[
              "변경사항이 있습니다.",
              "저장하지 않고 나가시겠습니까?",
              "변경사항이 모두 사라집니다."
            ]}
            confirmText="나가기"
            cancelText="저장"
            icon="!"
            iconBackground="var(--status-exempted)"
            onCancel={() => {
              setShowCancelConfirm(false);
              handleSave();
            }}
          />
        </div>
      )}
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 150px 0;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 533px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: auto;
  margin-bottom: 150px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SectionContent = styled.div`
  width: 100%;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Description = styled.p`
  margin: 0 0 20px 0;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: center;
`;

const TimeTagTabsContainer = styled.div`
  margin-bottom: 20px;
  background: transparent;
  border-radius: 8px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TimeTagTabRow = styled.div`
  display: flex;
  gap: 2px;
  flex-wrap: nowrap;
  justify-content: center;
  width: 100%;
`;

const TimeTagTab = styled.button<{ $active: boolean; $fullWidth?: boolean }>`
  flex: ${props => props.$fullWidth ? '1' : '1 1 0'};
  min-width: 0;
  padding: 4px 6px;
  border: none;
  background: ${props => props.$active ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$active ? '#fff' : 'var(--color-text-secondary)'};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: 28px;
  display: flex;
  align-items: center;
  
  /* 라이트 모드: 비활성 버튼 배경을 섹션보다 조금 더 진하게 */
  :root[data-theme='light'] & {
    background: ${props => props.$active ? 'var(--color-accent-strong)' : 'color-mix(in srgb, var(--color-section-bg) 88%, black)'};
  }
  /* 다크 모드: 비활성 버튼 배경을 섹션보다 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$active ? 'var(--color-accent-strong)' : 'color-mix(in srgb, var(--color-section-bg) 92%, white)'};
    color: ${props => props.$active ? '#ffffff' : 'var(--color-text-secondary)'};
  }
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${props => props.$active ? 'var(--color-accent)' : 'rgba(255, 255, 255, 0.08)'};
    
    /* 라이트 모드: active일 때 텍스트 흰색 유지 */
    :root[data-theme='light'] & {
      color: ${props => props.$active ? '#fff' : 'var(--color-text-primary)'};
    }
    
    /* 다크 모드: 기존 유지 */
    :root:not([data-theme='light']) & {
      color: var(--color-text-primary);
    }
  }

  @media (max-width: 480px) {
    padding: 3px 4px;
    font-size: 10px;
    height: 26px;
  }
`;

const TagBlocksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TagBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  min-height: 60px;
  position: relative;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.3);
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    min-height: auto;
    padding: 12px;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
  }

  @media (max-width: 360px) {
    padding: 6px 8px;
  }
`;

const TagBlockContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  @media (max-width: 480px) {
    gap: 3px;
  }

  @media (max-width: 360px) {
    gap: 3px;
  }
`;

const TagLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 1 auto;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 8px;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const TagDayOfWeek = styled.div`
  background: rgba(var(--color-accent-rgb), 0.15);
  color: var(--color-text-primary);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid rgba(var(--color-accent-rgb), 0.4);
  min-width: fit-content;
  width: fit-content;
  text-align: center;
  flex-shrink: 0;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 4px 12px;
    min-width: fit-content;
    width: fit-content;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 4px 10px;
    min-width: fit-content;
    width: fit-content;
    font-size: 11px;
  }

  @media (max-width: 360px) {
    padding: 4px 8px;
    min-width: fit-content;
    width: fit-content;
    font-size: 11px;
  }
`;

const TagName = styled.div`
  font-weight: 600;
  color: var(--color-accent-text);
  font-size: 14px;
  min-width: 100px;
  text-align: left;
  flex: 1;

  @media (max-width: 480px) {
    min-width: 60px;
    font-size: 13px;
  }

  @media (max-width: 360px) {
    min-width: 50px;
    font-size: 12px;
  }
`;

const TagRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    justify-content: space-between;
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: 4px;
  }

  @media (max-width: 360px) {
    gap: 2px;
  }
`;

const TagTimeRange = styled.div`
  color: var(--color-text-primary);
  font-size: 14px;
  white-space: nowrap;
  text-align: right;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 120px;

  @media (max-width: 600px) {
    text-align: left;
    min-width: auto;
    flex: 1;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }

  @media (max-width: 360px) {
    font-size: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: 2px;
  }

  @media (max-width: 360px) {
    gap: 1px;
  }
`;

const EditButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DeleteButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: var(--color-danger);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const NewTagBlock = styled.div`
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NewTagContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  &:nth-child(2) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  text-align: center;
  transition: border-color 0.2s;
  background: transparent;
  color: var(--color-text-primary);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
`;


const DayOfWeekSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DayOfWeekRow = styled.div`
  display: flex;
  justify-content: center;
  width: calc(7 * 36px + 6 * 8px);
  margin: 0 auto 8px auto;

  @media (max-width: 480px) {
    width: calc(7 * 32px + 6 * 6px);
  }

  @media (max-width: 360px) {
    width: calc(7 * 32px + 6 * 4px);
  }
`;

const AllDaysButton = styled.button<{ selected: boolean }>`
  width: 100%;
  padding: 6px 16px;
  border: 2px solid ${props => props.selected ? 'transparent' : 'var(--color-border)'};
  border-radius: 9999px;
  background: ${props => props.selected ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.selected ? '#fff' : 'var(--color-text-secondary)'};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  :root:not([data-theme='light']) & {
    color: ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    border: 2px solid ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'};
    background: ${props => props.selected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }

  &:hover {
    transform: scale(1.02);
  }
`;

const DayOfWeekLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const DayOfWeekButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    gap: 6px;
  }

  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const DayOfWeekButton = styled.button<{ selected: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 26px;
  min-height: 26px;
  border: 2px solid ${props => props.selected ? 'transparent' : 'var(--color-border)'};
  border-radius: 50%;
  background: ${props => props.selected ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.selected ? '#fff' : 'var(--color-text-secondary)'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'};
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => props.selected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.selected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
    background: ${props => props.selected ? 'var(--color-accent)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const TimeInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimeInputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const TimeInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: transparent;
  position: relative;
  
  &:focus-within {
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const TimeGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 auto;
`;

const HourInput = styled.input<{ $disabled?: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.$disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 2px 0;
  border-radius: 8px;
  width: 40px;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const TimeSeparator = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  margin: 0 2px;
`;

const MinuteInput = styled.input<{ $disabled?: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.$disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 2px 0;
  border-radius: 8px;
  width: 40px;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const DurationInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: transparent;
  position: relative;

  &:focus-within {
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const DurationInput = styled.input<{ $disabled?: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.$disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 2px 0;
  border-radius: 8px;
  width: 80px;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--color-text-muted);
  }

  /* 숫자 입력 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const ResetButton = styled.button<{ $disabled?: boolean }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.$disabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.$disabled ? 'transparent' : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.$disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const AmPmToggle = styled.button<{ isAm: boolean }>`
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  white-space: nowrap;

  ${props => props.isAm && `
    background: var(--color-accent-strong);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 2px 6px rgba(var(--color-accent-rgb), 0.3);
  `}
  
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.7);
  }
  
  &:hover {
    background: ${props => props.isAm ? 'var(--color-accent)' : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelAddButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AddTagButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-accent)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

const ImportButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 12px;
  border: 2px dashed var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(var(--color-accent-rgb), 0.5);
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 다른 클래스 데이터 가져오기 모달 스타일
const ImportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10000;
  overflow-y: auto;
  padding: 60px 20px;
`;

const ImportModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: 0 auto;
`;

const ImportModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ImportModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const ImportModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const ImportModalBody = styled.div`
  padding: 24px;
`;

const ImportSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ImportLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const ImportClassSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ImportClassSelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const ImportClassSelectText = styled.span`
  flex: 1;
  text-align: left;
`;

const ImportClassDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const ImportClassDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: visible;
`;

const ImportClassDropdownOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: left;
  
  &:hover {
    background-color: var(--color-panel);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const ImportLoadingText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
  font-size: 14px;
`;

const ImportEmptyText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
  font-size: 14px;
`;

const ImportTimeTagGrid = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const ImportTimeTagBlock = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-surface);
  border: 1px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  min-height: 60px;
  max-height: 80px;
  cursor: pointer;

  &:hover {
    border-color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'rgba(var(--color-accent-rgb), 0.3)'};
  }

  @media (max-width: 480px) {
    padding: 4px 6px 4px 10px;
    min-height: 50px;
    max-height: 70px;
  }

  @media (max-width: 360px) {
    padding: 2px 4px 2px 8px;
    min-height: 48px;
    max-height: 65px;
  }
`;

const ImportTimeTagContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 8px;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const ImportTagLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 1 auto;
  min-width: 0;

  @media (max-width: 480px) {
    gap: 8px;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const ImportTimeTagDayOfWeek = styled.div`
  background: rgba(var(--color-accent-rgb), 0.15);
  color: var(--color-text-primary);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid rgba(var(--color-accent-rgb), 0.4);
  min-width: 50px;
  text-align: center;

  @media (max-width: 480px) {
    padding: 2px 6px;
    min-width: 40px;
    font-size: 11px;
  }

  @media (max-width: 360px) {
    padding: 2px 4px;
    min-width: 35px;
    font-size: 10px;
  }
`;

const ImportTimeTagName = styled.div`
  font-weight: 600;
  color: var(--color-accent-text);
  font-size: 14px;
  min-width: 100px;
  text-align: left;

  @media (max-width: 480px) {
    min-width: 60px;
    font-size: 13px;
  }

  @media (max-width: 360px) {
    min-width: 50px;
    font-size: 12px;
  }
`;

const ImportTimeTagTimeRange = styled.div`
  color: var(--color-text-primary);
  font-size: 14px;
  white-space: nowrap;
  text-align: right;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 120px;

  @media (max-width: 480px) {
    font-size: 13px;
    min-width: 100px;
  }

  @media (max-width: 360px) {
    font-size: 12px;
    min-width: 90px;
  }
`;

const ImportCheckIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ImportModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ImportModalButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  .button-text-full {
    display: inline;
  }

  .button-text-short {
    display: none;
  }

  @media (max-width: 480px) {
    .button-text-full {
      display: none;
    }

    .button-text-short {
      display: inline;
    }
  }
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      background: var(--color-border);
      color: var(--color-text-muted);
      cursor: not-allowed;
      opacity: 0.5;
    }
  ` : `
    background: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-text-muted);
      color: white;
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const AddButton = styled.button`
  width: 48px;
  height: 48px;
  background: var(--color-accent-strong);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);

  &:hover {
    background: var(--color-accent);
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(var(--color-accent-rgb), 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PlusIcon = styled.span`
  font-weight: bold;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: clamp(6px, 2vw, 12px);
  padding: clamp(12px, 3vw, 20px) clamp(12px, 3vw, 24px);
  border-top: 1px solid var(--color-border);
  background: var(--color-panel);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-accent);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-accent)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CreateAlarmButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  white-space: nowrap;

  &:hover {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CreateAlarmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const CreateAlarmModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
`;

const CreateAlarmModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const CreateAlarmModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CreateAlarmCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const CreateAlarmModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoMessage = styled.div`
  background: var(--color-surface-highlight);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.875rem;
  color: var(--color-text-primary);
  line-height: 1.5;
`;

const AlarmSetSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AlarmSetSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.875rem;
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
`;

const DaySelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DaySelectorGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DaySelectButton = styled.button<{ $isSelected: boolean }>`
  width: 40px;
  height: 40px;
  min-width: 32px;
  min-height: 32px;
  border: 2px solid ${props => props.$isSelected ? 'transparent' : 'var(--color-border)'};
  border-radius: 50%;
  background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$isSelected ? 'white' : 'var(--color-text-secondary)'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'};
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }
  
  &:hover {
    background: ${props => props.$isSelected ? 'var(--color-accent)' : 'rgba(255, 255, 255, 0.08)'};
  }
  
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;


const CreateAlarmModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
  background: var(--color-panel);
`;

// 타임 태그 생성 모달 스타일 컴포넌트들
const CreateTagModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const CreateTagModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
`;

const CreateTagModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const CreateTagModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CreateTagCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const CreateTagModalBody = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const CreateTagModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
  background: var(--color-panel);
`;

const InputWithCounter = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CharCounter = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  text-align: right;
  margin-top: 2px;
`;

export default TimeTagSettingsModal;